<app-catalog-page-card title="COUNTRY">
  <app-catalog-table-toolbar
    datatableId="kt_country_datatable"
    searchPlaceholder="Search country records..."
    addButtonLabel="Add Country"
    addModalId="create_country_modal"
    refreshAriaLabel="Refresh countries"
    [disableRefresh]="isLoading()"
    (refresh)="loadRows()"
  ></app-catalog-table-toolbar>

  <app-catalog-crud-modal
    modalId="create_country_modal"
    title="Create Country"
    submitLabel="Create Country"
    [submitting]="isCreating()"
    [formError]="createFormError()"
    (submit)="onCreate()"
    (cancel)="resetCreateForm()"
  >
    <div modal-body>
      <form class="grid gap-4" autocomplete="off" [formGroup]="createForm">
        @if (lookupError()) {
          <div class="kt-alert kt-alert-destructive" role="alert">
            <div class="kt-alert-text">{{ lookupError() }}</div>
          </div>
        }

        <div class="grid gap-1.5">
          <label class="kt-form-label" for="create-country-name">
            Name <span class="catalog-required-marker" aria-hidden="true">*</span>
          </label>
          <input
            id="create-country-name"
            type="text"
            class="kt-input"
            maxlength="60"
            formControlName="name"
            [attr.aria-invalid]="shouldShowCreateFieldError('name')"
          />

          @if (shouldShowCreateRequiredError('name')) {
            <div class="catalog-field-error">Name is required.</div>
          }

          @if (shouldShowCreateMaxLengthError('name')) {
            <div class="catalog-field-error">Name cannot exceed 60 characters.</div>
          }
        </div>

        <div class="grid gap-1.5">
          <label class="kt-form-label" for="create-country-code">
            Code <span class="catalog-required-marker" aria-hidden="true">*</span>
          </label>
          <input
            id="create-country-code"
            type="text"
            class="kt-input"
            maxlength="20"
            formControlName="code"
            [attr.aria-invalid]="shouldShowCreateFieldError('code')"
          />

          @if (shouldShowCreateRequiredError('code')) {
            <div class="catalog-field-error">Code is required.</div>
          }

          @if (shouldShowCreateMaxLengthError('code')) {
            <div class="catalog-field-error">Code cannot exceed 20 characters.</div>
          }
        </div>

        <div class="grid gap-1.5">
          <label class="kt-form-label" for="create-country-region">Region</label>
          <select id="create-country-region" class="kt-select" formControlName="regionId">
            <option value="">Select region...</option>
            @for (item of regions(); track item.id) {
              <option [value]="item.id">{{ item.name }}</option>
            }
          </select>
        </div>
      </form>
    </div>
  </app-catalog-crud-modal>

  <button
    type="button"
    class="hidden"
    aria-hidden="true"
    tabindex="-1"
    data-kt-modal-toggle="#edit_country_modal"
  ></button>

  <app-catalog-crud-modal
    modalId="edit_country_modal"
    title="Edit Country"
    submitLabel="Save Changes"
    [submitting]="isUpdating()"
    [formError]="editFormError()"
    (submit)="onUpdate()"
    (cancel)="resetEditForm()"
  >
    <div modal-body>
      <form class="grid gap-4" autocomplete="off" [formGroup]="editForm">
        @if (lookupError()) {
          <div class="kt-alert kt-alert-destructive" role="alert">
            <div class="kt-alert-text">{{ lookupError() }}</div>
          </div>
        }

        <div class="grid gap-1.5">
          <label class="kt-form-label" for="edit-country-name">
            Name <span class="catalog-required-marker" aria-hidden="true">*</span>
          </label>
          <input
            id="edit-country-name"
            type="text"
            class="kt-input"
            maxlength="60"
            formControlName="name"
            [attr.aria-invalid]="shouldShowEditFieldError('name')"
          />

          @if (shouldShowEditRequiredError('name')) {
            <div class="catalog-field-error">Name is required.</div>
          }

          @if (shouldShowEditMaxLengthError('name')) {
            <div class="catalog-field-error">Name cannot exceed 60 characters.</div>
          }
        </div>

        <div class="grid gap-1.5">
          <label class="kt-form-label" for="edit-country-code">
            Code <span class="catalog-required-marker" aria-hidden="true">*</span>
          </label>
          <input
            id="edit-country-code"
            type="text"
            class="kt-input"
            maxlength="20"
            formControlName="code"
            [attr.aria-invalid]="shouldShowEditFieldError('code')"
          />

          @if (shouldShowEditRequiredError('code')) {
            <div class="catalog-field-error">Code is required.</div>
          }

          @if (shouldShowEditMaxLengthError('code')) {
            <div class="catalog-field-error">Code cannot exceed 20 characters.</div>
          }
        </div>

        <div class="grid gap-1.5">
          <label class="kt-form-label" for="edit-country-region">Region</label>
          <select id="edit-country-region" class="kt-select" formControlName="regionId">
            <option value="">Select region...</option>
            @for (item of regions(); track item.id) {
              <option [value]="item.id">{{ item.name }}</option>
            }
          </select>
        </div>
      </form>
    </div>
  </app-catalog-crud-modal>

  <button
    type="button"
    class="hidden"
    aria-hidden="true"
    tabindex="-1"
    data-kt-modal-toggle="#delete_country_confirm_modal"
  ></button>

  <app-catalog-delete-confirm-modal
    modalId="delete_country_confirm_modal"
    title="Remove Country"
    message="Are you sure you want to remove this country?"
    confirmLabel="Remove Country"
    [confirming]="deletingId() !== null"
    (confirm)="confirmDelete()"
    (cancel)="cancelDelete()"
  ></app-catalog-delete-confirm-modal>

  @if (hasAlert()) {
    <div
      class="kt-alert kt-alert-light"
      [class.kt-alert-success]="alertVariant() === 'success'"
      [class.kt-alert-destructive]="alertVariant() === 'destructive'"
      role="alert"
    >
      <div class="kt-alert-icon">
        <i class="ki-filled ki-information" aria-hidden="true"></i>
      </div>

      <div class="grow">
        <div class="kt-alert-title"><strong>{{ alertTitle() }}</strong></div>

        @if (alertMessage()) {
          <div class="kt-alert-text">{{ alertMessage() }}</div>
        }
      </div>

      <div class="kt-alert-toolbar">
        <div class="kt-alert-actions">
          <button
            type="button"
            class="kt-alert-close"
            aria-label="Dismiss alert"
            (click)="dismissAlert()"
          >
            <i class="ki-filled ki-cross" aria-hidden="true"></i>
          </button>
        </div>
      </div>
    </div>
  }

  @if (isLoading()) {
    <div class="catalog-loading" role="status" aria-live="polite" aria-label="Loading countries">
      <span class="catalog-loading-spinner" aria-hidden="true"></span>
      <span class="catalog-loading-text">Loading...</span>
    </div>
  }

  @if (!isLoading() && errorMessage()) {
    <div class="kt-alert kt-alert-destructive flex items-center justify-between gap-3">
      <span>{{ errorMessage() }}</span>
      <button type="button" class="kt-btn kt-btn-sm kt-btn-light" (click)="loadRows()">Retry</button>
    </div>
  }

  @if (!isLoading() && !errorMessage()) {
    <div
      id="kt_country_datatable"
      class="kt-card-table"
      data-kt-datatable="true"
      data-kt-datatable-page-size="20"
      data-kt-datatable-state-save="true"
      data-kt-datatable-state-namespace="country-datatable"
      (click)="onTableActionClick($event)"
    >
      <div class="kt-table-wrapper kt-scrollable">
        <table class="kt-table" data-kt-datatable-table="true">
          <thead>
            <tr>
              <th scope="col" data-kt-datatable-column="name">
                <span class="kt-table-col">
                  <span class="kt-table-col-label">Name</span>
                  <span class="kt-table-col-sort"></span>
                </span>
              </th>
              <th scope="col" data-kt-datatable-column="region">
                <span class="kt-table-col">
                  <span class="kt-table-col-label">Region</span>
                  <span class="kt-table-col-sort"></span>
                </span>
              </th>
              <th scope="col" data-kt-datatable-column="code">
                <span class="kt-table-col">
                  <span class="kt-table-col-label">Code</span>
                  <span class="kt-table-col-sort"></span>
                </span>
              </th>
              <th
                scope="col"
                data-kt-datatable-column="actions"
                data-kt-datatable-column-sort="false"
                class="text-end"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            @if (rows().length === 0) {
              <tr>
                <td colspan="4" class="text-center text-secondary-foreground py-6">
                  No countries found.
                </td>
              </tr>
            } @else {
              @for (item of rows(); track item.id) {
                <tr>
                  <td>{{ item.name }}</td>
                  <td>{{ item.regionName || '-' }}</td>
                  <td>{{ item.code }}</td>
                  <td class="text-end">
                    <span class="inline-flex gap-2.5">
                      <button
                        type="button"
                        class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                        aria-label="Edit country"
                        data-action="edit-country"
                        [attr.data-country-id]="item.id"
                        [disabled]="deletingId() !== null || isUpdating()"
                      >
                        <i class="ki-filled ki-pencil" aria-hidden="true"></i>
                      </button>
                      <button
                        type="button"
                        class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                        aria-label="Remove country"
                        data-action="delete-country"
                        [attr.data-country-id]="item.id"
                        [disabled]="deletingId() !== null"
                      >
                        <i class="ki-filled ki-trash" aria-hidden="true"></i>
                      </button>
                    </span>
                  </td>
                </tr>
              }
            }
          </tbody>
        </table>
      </div>

      <div class="flex items-center justify-between gap-4 flex-wrap px-5 py-4">
        <div data-kt-datatable-info="true" class="kt-datatable-info text-sm text-secondary-foreground"></div>

        <div class="kt-datatable-length flex items-center gap-3">
          <select data-kt-datatable-size="true" class="kt-select kt-select-sm w-20"></select>
          <div data-kt-datatable-pagination="true" class="kt-datatable-pagination flex items-center gap-2"></div>
        </div>
      </div>
    </div>
  }
</app-catalog-page-card>
